import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Tooltip,
  Button,
  Input,
  Card,
  CardBody,
  Tab,
  Tabs,
  SortDescriptor
} from "@nextui-org/react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./datepicker-custom.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import { EyeIcon, DeleteIcon, SearchIcon } from "@/components/icons";
import DefaultLayout from "@/layouts/default";
import { inventoryAPI, booksAPI } from "@/services/api";
import DataTable, { Column } from "@/components/DataTable";
import { formatDate } from "@/utils/dateUtils";

interface InventoryItem {
  id: string;
  bookName?: string;
  bookId?: string;
  quantity: number;
  price?: number; // Purchase price per item from inventory record
  pricePerBook?: number; // Price per book from inventory record
  totalCost: number;
  date: string;
  created_at?: string;
  // Book information
  book_name?: string;
  book_author?: string;
  book_category?: string;
  book_version?: number;
  book_parent_id?: string | number;
  book_current_stock?: number;
  book_current_price?: number;
  book_current_price_per_book?: number;
  // Inventory-specific data
  inventory_price?: number;
  inventory_price_per_book?: number;
  inventory_total_cost?: number;
  inventory_date?: string;
}

interface Book {
  id: string;
  name: string;
  version?: string;
  book_version?: string;
  parent_book_id?: string | number;
  stock?: number;
  price?: number;
  price_per_book?: number;
}

// Interface for metrics by timeframe
interface CostMetrics {
  total: number;
  items: number;
  books: number;
}

const formatPrice = (value: number | string): string => {
  const numericValue = typeof value === "string" ? parseFloat(value) : value;

  return !isNaN(numericValue) ? Math.round(numericValue).toString() : "0";
};

const InventoryPage: React.FC = () => {
  const { t } = useTranslation();
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<InventoryItem[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>("total");
  const [costMetrics, setCostMetrics] = useState<Record<string, CostMetrics>>({
    total: { total: 0, items: 0, books: 0 },
    monthly: { total: 0, items: 0, books: 0 },
    daily: { total: 0, items: 0, books: 0 },
  });
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "date",
    direction: "descending"
  });
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBooks = async () => {
    try {
      const clientId = localStorage.getItem("clientId");

      if (!clientId) {
        return {};
      }

      // Now we can pass clientId parameter as the API supports it
      const booksData = await booksAPI.getAll(clientId);

      // Check data structure
      if (!Array.isArray(booksData)) {
        return {};
      }

      // Create a lookup map for books
      const booksMap: Record<string, Book> = {};

      if (Array.isArray(booksData)) {
        booksData.forEach((book) => {
          // Get ID in the right format
          const bookId = String(book.id);
          // Convert book.id to string to ensure consistent lookup
          booksMap[bookId] = book;
        });
      }

      return booksMap;
    } catch (error) {
      return {};
    }
  };

  // Calculate costs by timeframe
  const calculateCostsByTimeframe = (items: InventoryItem[]) => {
    console.log("Calculating costs from items:", items);

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for comparison

    // Daily metrics (today only)
    const dailyItems = items.filter((item) => {
      const itemDate = new Date(item.date);

      itemDate.setHours(0, 0, 0, 0);

      return itemDate.getTime() === today.getTime();
    });

    // Monthly metrics (current month)
    const monthlyItems = items.filter((item) => {
      const itemDate = new Date(item.date);

      return (
        itemDate.getMonth() === today.getMonth() &&
        itemDate.getFullYear() === today.getFullYear()
      );
    });

    // Calculate metrics for each timeframe
    const calculateMetrics = (itemsList: InventoryItem[]): CostMetrics => {
      return {
        total: itemsList.reduce((sum, item) => sum + item.totalCost, 0),
        items: itemsList.length,
        books: itemsList.reduce((sum, item) => sum + item.quantity, 0),
      };
    };

    const metrics = {
      daily: calculateMetrics(dailyItems),
      monthly: calculateMetrics(monthlyItems),
      total: calculateMetrics(items),
    };

    console.log("Calculated metrics by timeframe:", metrics);

    setCostMetrics(metrics);
  };

  // Handle search using nextUI's Input component
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    filterInventoryItems(inventoryItems, value, startDate, endDate);
  };

  // Filter inventory items based on search, date range, and calculate metrics
  const filterInventoryItems = (
    allItems: InventoryItem[],
    query: string = searchQuery,
    start: Date | null = startDate,
    end: Date | null = endDate,
  ) => {
    let filtered = [...allItems];

    // Filter by date range if specified
    if (start && end) {
      const startStr = start.toISOString();
      const endStr = end.toISOString();

      filtered = filtered.filter((item) => {
        const itemDate = new Date(item.date).toISOString();

        return itemDate >= startStr && itemDate <= endStr;
      });
    }

    // Filter by search query
    if (query.trim() !== "") {
      const lowercaseQuery = query.toLowerCase();

      filtered = filtered.filter((item) => {
        const bookName = item.book_name || item.bookName || "";
        const bookAuthor = item.book_author || "";
        const bookCategory = item.book_category || "";

        return (
          bookName.toLowerCase().includes(lowercaseQuery) ||
          bookAuthor.toLowerCase().includes(lowercaseQuery) ||
          bookCategory.toLowerCase().includes(lowercaseQuery) ||
          item.quantity.toString().includes(lowercaseQuery) ||
          (item.price ? item.price.toString().includes(lowercaseQuery) : false) ||
          (item.pricePerBook ? item.pricePerBook.toString().includes(lowercaseQuery) : false) ||
          item.totalCost.toString().includes(lowercaseQuery) ||
          formatDate(item.date).toLowerCase().includes(lowercaseQuery) ||
          (item.book_version ? item.book_version.toString().includes(lowercaseQuery) : false)
        );
      });
    }

    // Ensure results are always sorted by date (newest first)
    filtered.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );

    setFilteredItems(filtered);
    calculateCostsByTimeframe(filtered);
    
    // Reset sort descriptor to ensure consistent serial numbering
    setSortDescriptor({
      column: "date",
      direction: "descending"
    });
  };

  // Handle sort change
  const handleSortChange = (descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor);
  };

  const fetchInventoryItems = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const clientId = localStorage.getItem("clientId");

      console.log("Client ID from localStorage:", clientId);

      if (!clientId) {
        setError("Client ID not found in local storage. Please log in again.");
        setIsLoading(false);

        return;
      }

      // Ensure we have books data for name lookup
      console.log("Fetching books data first...");
      const booksMap = await fetchBooks();

      console.log("BOOKS DATA RECEIVED:", booksMap);

      // Fetch inventory items from API
      console.log("Now fetching inventory data for client:", clientId);
      const items = await inventoryAPI.getAll(clientId);

      console.log("RAW INVENTORY DATA FROM API:", items);

      // Examine the first item to understand its structure
      if (items && items.length > 0) {
        console.log("SAMPLE INVENTORY ITEM STRUCTURE:", items[0]);
        // Check if book_id is nested differently
        const possibleBookIdFields = ["book_id", "bookId", "book", "id"];

        console.log("Checking possible book ID fields:");
        for (const field of possibleBookIdFields) {
          console.log(`- ${field}:`, items[0][field]);
        }
      }

      if (!items || !Array.isArray(items)) {
        setError("Invalid data received from server");
        setIsLoading(false);

        return;
      }

      // Process inventory items to ensure properties are correctly formatted
      const processedItems = items.map((item) => {
        // Use inventory-specific prices (what was actually paid at that time)
        const price = Number(item.price);
        const pricePerBook = Number(item.price_per_book);
        const quantity = Number(item.quantity);
        
        // Calculate total cost from inventory data
        const totalCost =  (quantity * price);

        // Get book information from the enhanced response
        const bookName = item.book_name || item.bookName || "";
        const bookId = item.bookId || item.book_id || (item.book && item.book.id);
        const stringBookId = bookId ? String(bookId) : null;

        // Process date fields
        const dateValue = item.inventory_date || item.date || item.created_at || null;
        const createdAtValue = item.created_at || item.createdAt || null;

        console.log(`Processing inventory item ${item.id}:`, {
          originalItem: item,
          processedBookId: stringBookId,
          bookName: bookName,
          inventoryPrice: price,
          inventoryPricePerBook: pricePerBook,
          inventoryTotalCost: totalCost,
          processedDate: dateValue,
          formattedDate: formatDate(dateValue),
          quantity,
          bookVersion: item.book_version,
          bookParentId: item.book_parent_id
        });

        // Return the processed item with all inventory and book data
        return {
          ...item,
          id: String(item.id),
          bookId: stringBookId,
          bookName: bookName,
          quantity,
          price, // Inventory price (what was paid)
          pricePerBook, // Inventory price per book (what was paid)
          totalCost,
          date: dateValue,
          created_at: createdAtValue,
          // Book information for version display
          book_name: item.book_name,
          book_author: item.book_author,
          book_category: item.book_category,
          book_version: item.book_version,
          book_parent_id: item.book_parent_id,
          book_current_stock: item.book_current_stock,
          book_current_price: item.book_current_price,
          book_current_price_per_book: item.book_current_price_per_book
        };
      });

      console.log("PROCESSED inventory items:", processedItems);

      // Sort by date (newest first)
      processedItems.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      );

      setInventoryItems(processedItems);

      // Apply filtering and calculate metrics
      filterInventoryItems(processedItems);

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching inventory items:", error);
      setError(
        `Failed to fetch inventory data: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
      setInventoryItems([]);
      setFilteredItems([]);
      setIsLoading(false);
    }
  };

  // Add diagnostic function to deeply inspect one inventory item
  const checkSingleInventoryItem = async () => {
    try {
      const items = await inventoryAPI.getAll();

      if (!items || items.length === 0) {
        console.warn("No inventory items found to inspect");

        return;
      }

      // Get the first item's ID
      const itemId = items[0].id;

      console.log("🔍 DIAGNOSTIC: Inspecting inventory item:", itemId);

      // Fetch that specific item
      const singleItem = await inventoryAPI.getById(itemId);

      console.log("🔍 DIAGNOSTIC: Full inventory item data:", singleItem);

      // Try to fetch the associated book
      const bookId = singleItem.book_id || singleItem.bookId;

      if (bookId) {
        console.log(`🔍 DIAGNOSTIC: Fetching associated book (ID: ${bookId})`);
        const bookData = await booksAPI.getById(bookId);

        console.log("🔍 DIAGNOSTIC: Associated book data:", bookData);
      } else {
        console.warn("🔍 DIAGNOSTIC: No book ID found in inventory item");
      }
    } catch (error) {
      console.error("🔍 DIAGNOSTIC ERROR:", error);
    }
  };

  useEffect(() => {
    fetchInventoryItems();

    // Run diagnostic check after a short delay
    setTimeout(() => {
      checkSingleInventoryItem();
    }, 3000);
  }, []);

  useEffect(() => {
    filterInventoryItems(inventoryItems, searchQuery, startDate, endDate);
  }, [startDate, endDate, searchQuery]);

  const handleView = (id: string) => {
    navigate(`/inventory/view/${id}`);
  };

  // const handleEdit = (id: string) => {
  //   navigate(`/inventory/edit/${id}`);
  // };

  const handleDelete = async (id: string) => {
    if (
      window.confirm(t("DeleteInventoryConfirm"))
    ) {
      try {
        await inventoryAPI.delete(id);
        // Refresh inventory items after deletion
        fetchInventoryItems();
      } catch (error) {
        console.error("Error deleting inventory item:", error);
        alert(
          `${t("DeleteError")}: ${error instanceof Error ? error.message : t("UnknownError")}`,
        );
      }
    }
  };

  const handleRefresh = () => {
    setStartDate(null);
    setEndDate(null);
    setSearchQuery("");
    fetchInventoryItems();
  };

  // Get metrics for the selected timeframe
  const getSelectedMetrics = () => {
    return costMetrics[selectedTimeframe] || costMetrics.total;
  };

  const metrics = getSelectedMetrics();

  // Helper function to fix RTL text with brackets/parentheses
  const fixRTLBrackets = (text: string): string => {
    if (!text) return text;
    // Wrap brackets and their content in LTR direction span
    // This ensures brackets display correctly in RTL context
    return text.replace(/\(([^)]+)\)/g, '<span dir="ltr" style="direction: ltr; unicode-bidi: embed;">($1)</span>');
  };

  // Download PDF report function with RTL and Urdu support
  const downloadPDF = async () => {
    if (filteredItems.length === 0) {
      alert(t("NoDataToDownload", "No inventory data available to download."));
      return;
    }

    setIsDownloading(true);
    setError(null);

    try {
      // Preload and wait for the Urdu font to be ready
      const fontUrl = '/fonts/Mehr_Nastaliq_Web v.2.0.woff';
      const fontName = 'Mehr Nastaliq';
      
      // Check if font is already loaded, if not, load it
      try {
        if (!document.fonts.check(`16px "${fontName}"`)) {
          const fontFace = new FontFace(fontName, `url(${fontUrl})`);
          await fontFace.load();
          document.fonts.add(fontFace);
          // Wait a bit more to ensure font is fully ready for rendering
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      } catch (fontError) {
        console.warn("Font loading error, continuing with fallback:", fontError);
      }

      // Create a temporary container for the PDF content with RTL support
      const pdfContainer = document.createElement("div");
      pdfContainer.style.position = "absolute";
      pdfContainer.style.left = "-9999px";
      pdfContainer.style.top = "0";
      pdfContainer.style.width = "210mm"; // A4 width
      pdfContainer.style.padding = "15mm";
      pdfContainer.style.backgroundColor = "white";
      pdfContainer.style.direction = "rtl";
      pdfContainer.style.fontFamily = `"${fontName}", "Mehr Nastaliq Web", Arial, Tahoma, sans-serif`;
      pdfContainer.style.fontSize = "11px";
      pdfContainer.style.color = "#000";
      pdfContainer.style.overflowX = "hidden";
      pdfContainer.style.overflowY = "hidden";
      pdfContainer.style.boxSizing = "border-box";
      pdfContainer.dir = "rtl";

      // Add font-face style to the container
      const fontStyle = document.createElement("style");
      const baseUrl = window.location.origin;
      fontStyle.textContent = `
        @font-face {
          font-family: '${fontName}';
          src: url('${baseUrl}${fontUrl}') format('woff');
          font-weight: normal;
          font-style: normal;
          font-display: block;
        }
        @font-face {
          font-family: 'Mehr Nastaliq Web';
          src: url('${baseUrl}${fontUrl}') format('woff');
          font-weight: normal;
          font-style: normal;
          font-display: block;
        }
        * {
          font-family: "${fontName}", "Mehr Nastaliq Web", Arial, Tahoma, sans-serif !important;
          box-sizing: border-box;
        }
        table {
          table-layout: fixed !important;
          width: 100% !important;
          max-width: 100% !important;
        }
        colgroup col:nth-child(1) {
          width: 5% !important;
          min-width: 5% !important;
          max-width: 5% !important;
        }
        colgroup col:nth-child(2) {
          width: 35% !important;
          min-width: 35% !important;
          max-width: 35% !important;
        }
        colgroup col:nth-child(3),
        colgroup col:nth-child(4),
        colgroup col:nth-child(5),
        colgroup col:nth-child(6) {
          width: 12% !important;
          min-width: 12% !important;
          max-width: 12% !important;
        }
        th, td {
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
        }
        th:nth-child(1), td:nth-child(1) {
          width: 5% !important;
        }
        th:nth-child(2), td:nth-child(2) {
          width: 35% !important;
        }
        th:nth-child(3), td:nth-child(3),
        th:nth-child(4), td:nth-child(4),
        th:nth-child(5), td:nth-child(5),
        th:nth-child(6), td:nth-child(6) {
          width: 12% !important;
        }
      `;
      pdfContainer.appendChild(fontStyle);

      // Helper function to create table HTML with header
      const createTableHTML = (rows: InventoryItem[], startIndex: number) => {
        const fontFamilyStr = `"${fontName}", "Mehr Nastaliq Web", Arial, Tahoma, sans-serif`;
        const headerHTML = `
          <thead>
            <tr style="background-color: #4285c4; color: white;">
              <th style="border: 1px solid #ddd; padding: 6px; text-align: center; font-weight: bold; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word;">${t("S.No", "نمبر")}</th>
              <th style="border: 1px solid #ddd; padding: 6px; text-align: right; font-weight: bold; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word;">${t("BookName", "کتاب کا نام")}</th>
              <th style="border: 1px solid #ddd; padding: 6px; text-align: center; font-weight: bold; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word;">${t("VersionQuantity", "مقدار")}</th>
              <th style="border: 1px solid #ddd; padding: 6px; text-align: center; font-weight: bold; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word;">${t("Price", "قیمت")}</th>
              <th style="border: 1px solid #ddd; padding: 6px; text-align: center; font-weight: bold; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word;">${t("TotalCost", "کل لاگت")}</th>
              <th style="border: 1px solid #ddd; padding: 6px; text-align: center; font-weight: bold; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word;">${t("VersionDate", "تاریخ")}</th>
            </tr>
          </thead>
        `;

        const rowsHTML = rows
          .map(
            (item, idx) => {
              // Fix RTL brackets in book name
              const bookName = item.book_name || item.bookName || t("UnknownBook");
              const fixedBookName = fixRTLBrackets(bookName);
              
              return `
              <tr style="background-color: ${idx % 2 === 0 ? "#f9f9f9" : "white"};">
                <td style="border: 1px solid #ddd; padding: 6px; text-align: center; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word;">${startIndex + idx + 1}</td>
                <td style="border: 1px solid #ddd; padding: 6px; text-align: right; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word; direction: rtl; unicode-bidi: embed;">${fixedBookName}</td>
                <td style="border: 1px solid #ddd; padding: 6px; text-align: center; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word;">${item.quantity || 0}</td>
                <td style="border: 1px solid #ddd; padding: 6px; text-align: center; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word;">${formatPrice(item.price || 0)}</td>
                <td style="border: 1px solid #ddd; padding: 6px; text-align: center; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word;">${formatPrice(item.totalCost || 0)}</td>
                <td style="border: 1px solid #ddd; padding: 6px; text-align: center; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word;">${item.date ? formatDate(item.date) : t("MissingDate")}</td>
              </tr>
            `;
            }
          )
          .join("");

        return `
          <table style="width: 100%; max-width: 100%; border-collapse: collapse; direction: rtl; table-layout: fixed;">
            <colgroup>
              <col style="width: 5%;">
              <col style="width: 35%;">
              <col style="width: 12%;">
              <col style="width: 12%;">
              <col style="width: 12%;">
              <col style="width: 12%;">
            </colgroup>
            ${headerHTML}
            <tbody>
              ${rowsHTML}
            </tbody>
          </table>
        `;
      };

      // Create header HTML (title, date range, summary)
      const now = new Date();
      const formattedStartDate = startDate 
        ? new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000).toISOString().split("T")[0]
        : "";
      const formattedEndDate = endDate
        ? new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000).toISOString().split("T")[0]
        : "";
      
      const dateRangeText = startDate && endDate
        ? `${formattedStartDate} ${t("To", "سے")} ${formattedEndDate} ${t("To", "تک")}`
        : t("AllInventory", "تمام انوینٹری");

      const headerHTML = `
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="font-size: 24px; margin-bottom: 15px; color: #333; font-family: '${fontName}', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">
            ${t("Inventory", "انوینٹری کی رپورٹ")}
          </h1>
          <p style="font-size: 14px; margin: 5px 0; font-family: '${fontName}', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">
            <strong>${t("ReportPeriod", "رپورٹ کا دورانیہ")}:</strong> ${dateRangeText}
          </p>
          <p style="font-size: 12px; margin: 5px 0; color: #666; font-family: '${fontName}', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">
            ${t("GeneratedOn", "وقت اجراء رپورٹ")}: ${now.toLocaleDateString("ur-PK")} - ${now.toLocaleTimeString("ur-PK")}
          </p>
          ${searchQuery ? `<p style="font-size: 12px; margin: 5px 0; color: #666; font-family: '${fontName}', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("Filtering by", "فلٹر")}: ${searchQuery}</p>` : ""}
        </div>

        <div style="margin-bottom: 30px; display: flex; justify-content: space-around; flex-wrap: wrap; gap: 15px;">
          <div style="border: 1px solid #ddd; padding: 15px; border-radius: 8px; text-align: center; min-width: 120px; flex: 1;">
            <h3 style="font-size: 14px; margin: 0 0 10px 0; color: #666; font-family: '${fontName}', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("InventoryItems", "کل آئٹمز")}</h3>
            <p style="margin: 0; font-size: 18px; font-weight: bold; font-family: '${fontName}', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${filteredItems.length}</p>
          </div>
          <div style="border: 1px solid #ddd; padding: 15px; border-radius: 8px; text-align: center; min-width: 120px; flex: 1;">
            <h3 style="font-size: 14px; margin: 0 0 10px 0; color: #666; font-family: '${fontName}', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("TotalInventoryCost", "کل لاگت")}</h3>
            <p style="margin: 0; font-size: 18px; font-weight: bold; font-family: '${fontName}', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${formatPrice(metrics.total)}</p>
          </div>
          <div style="border: 1px solid #ddd; padding: 15px; border-radius: 8px; text-align: center; min-width: 120px; flex: 1;">
            <h3 style="font-size: 14px; margin: 0 0 10px 0; color: #666; font-family: '${fontName}', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("BooksPurchased", "کل کتابیں")}</h3>
            <p style="margin: 0; font-size: 18px; font-weight: bold; font-family: '${fontName}', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${metrics.books}</p>
          </div>
        </div>
      `;

      // Estimate rows per page
      const rowsPerPageFirst = 20;
      const rowsPerPageSubsequent = 24;

      // Create PDF
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210; // A4 width in mm

      // Split data into chunks and create pages
      const totalRows = filteredItems.length;
      let currentIndex = 0;
      let pageNumber = 0;

      while (currentIndex < totalRows) {
        const rowsPerPage = pageNumber === 0 ? rowsPerPageFirst : rowsPerPageSubsequent;
        const chunk = filteredItems.slice(currentIndex, currentIndex + rowsPerPage);
        
        // Create page content
        pdfContainer.innerHTML = pageNumber === 0
          ? headerHTML + createTableHTML(chunk, currentIndex)
          : `<div style="margin-bottom: 10px;"></div>` + createTableHTML(chunk, currentIndex);

        document.body.appendChild(pdfContainer);
        
        // Wait for font to render properly
        await new Promise((resolve) => setTimeout(resolve, 500));

        try {
          const canvas = await html2canvas(pdfContainer, {
            scale: 1.5,
            useCORS: true,
            logging: false,
            backgroundColor: "#ffffff",
            windowWidth: pdfContainer.scrollWidth,
            windowHeight: pdfContainer.scrollHeight,
            removeContainer: false,
            onclone: (clonedDoc) => {
              const clonedContainer = clonedDoc.querySelector('div[dir="rtl"]');
              if (clonedContainer) {
                (clonedContainer as HTMLElement).style.fontFamily = `"${fontName}", "Mehr Nastaliq Web", Arial, Tahoma, sans-serif`;
              }
            },
          });

          document.body.removeChild(pdfContainer);

          // Add to PDF
          if (pageNumber > 0) {
            pdf.addPage();
          }

          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          const imgData = canvas.toDataURL("image/jpeg", 0.92);
          pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight);

          currentIndex += rowsPerPage;
          pageNumber++;
        } catch (canvasError) {
          if (pdfContainer.parentNode) {
            document.body.removeChild(pdfContainer);
          }
          throw canvasError;
        }
      }

      // Generate filename
      const filename = startDate && endDate
        ? `Inventory_Report_${formattedStartDate}_to_${formattedEndDate}.pdf`
        : `Inventory_Report_${now.toISOString().split("T")[0]}.pdf`;

      // Save the PDF
      pdf.save(filename);
      console.log(`PDF report downloaded as: ${filename}`);
    } catch (error: any) {
      console.error("PDF generation error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to generate PDF report";
      setError(
        t("ErrorDownloadingReport", "Error downloading report: ") + errorMessage
      );
      alert(t("ErrorDownloadingReport", "Error downloading report: ") + errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  // Define columns for DataTable
  const columns: Column<InventoryItem>[] = [
    { 
      key: "bookName", 
      label: t("BookName"),
      sortable: true,
      renderCell: (item) => {
        if (item.book_name || item.bookName) {
          return (
            <span className="font-medium">
              {item.book_name || item.bookName}
            </span>
          );
        } else {
          return (
            <div>
              <span className="text-red-500">
                {t("UnknownBook")}
              </span>
              <span className="block text-xs text-gray-500">
                ID: {item.bookId || t("Missing")}
              </span>
            </div>
          );
        }
      }
    },
    {
      key: "created_at",
      label: t("VersionDate"),
      sortable: true,
      renderCell: (item) => {
        // Display created_at date if available, otherwise use the date field
        const dateToFormat = item.created_at || item.date;
        return dateToFormat ? 
          formatDate(dateToFormat) : 
          <span className="text-red-500">{t("MissingDate")}</span>;
      }
    },
    { 
      key: "quantity", 
      label: t("VersionQuantity"),
      sortable: true
    },
    
    {
      key: "price", 
      label: t("Price"),
      sortable: true,
      renderCell: (item) => formatPrice(item.price || 0)
    },
    { 
      key: "totalCost", 
      label: t("TotalCost"),
      sortable: true,
      renderCell: (item) => formatPrice(item.totalCost)
    },
    { 
      key: "pricePerBook", 
      label: t("pricePerBook"),
      sortable: true,
      renderCell: (item) => formatPrice(item.pricePerBook || 0)
    },
    { 
      key: "date", 
      label: t("Date"),
      sortable: true,
      renderCell: (item) => item.date ? 
        formatDate(item.date) : 
        <span className="text-red-500">{t("MissingDate")}</span>
    },
    { 
      key: "actions", 
      label: t("Actions"),
      renderCell: (item) => (
        <div className="flex items-center justify-center gap-2">
          <Tooltip content={t("ViewDetails")}>
            <span className="cursor-pointer text-lg text-primary hover:opacity-80">
              <EyeIcon onClick={() => handleView(item.id)} />
            </span>
          </Tooltip>
          {/* <Tooltip content={t("Edit")}>
            <span className="cursor-pointer text-lg text-blue-500 hover:opacity-80">
              <EditIcon onClick={() => handleEdit(item.id)} />
            </span>
          </Tooltip> */}
          <Tooltip color="danger" content={t("Delete")}>
            <span className="cursor-pointer text-lg text-danger hover:opacity-80">
              <DeleteIcon onClick={() => handleDelete(item.id)} />
            </span>
          </Tooltip>
        </div>
      )
    },
  ];

  return (
    <DefaultLayout>
      <h1 className="text-3xl font-bold text-center mb-8">{t("Inventory")}</h1>

      {/* Inventory Summary Cards with Timeframe Tabs */}
      <div className="px-8 mb-6">
        <Tabs
          aria-label="Time period options"
          className="mb-4"
          selectedKey={selectedTimeframe}
          onSelectionChange={(key) => setSelectedTimeframe(key as string)}
        >
          <Tab key="daily" title={t("Today")} />
          <Tab key="monthly" title={t("ThisMonth")} />
          <Tab key="total" title={t("AllTime")} />
        </Tabs>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardBody className="text-center">
              <p className="text-md text-default-500">
                {t("TotalInventoryCost")}
              </p>
              <h3 className="text-2xl font-bold">
                {formatPrice(metrics.total)}
              </h3>
              <p className="text-xs text-default-400">
                {t("InvestmentValue")}
              </p>
            </CardBody>
          </Card>

          <Card>
            <CardBody className="text-center">
              <p className="text-md text-default-500">{t("InventoryItems")}</p>
              <h3 className="text-2xl font-bold">{metrics.items}</h3>
              <p className="text-xs text-default-400">{t("Records")}</p>
            </CardBody>
          </Card>

          <Card>
            <CardBody className="text-center">
              <p className="text-md text-default-500">{t("BooksPurchased")}</p>
              <h3 className="text-2xl font-bold">{metrics.books}</h3>
              <p className="text-xs text-default-400">{t("Units")}</p>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Control bar with search, date filters and add button */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-4 px-8">
        <div className="flex w-full md:w-auto justify-between md:justify-start gap-2">
          <Button
            className="min-w-[140px]"
            color="primary"
            size="md"
            onClick={() => navigate("/inventory/add")}
          >
            {t("AddInventory")}
          </Button>
          <Button
            className="min-w-[140px]"
            color="success"
            variant="bordered"
            size="md"
            onClick={downloadPDF}
            isLoading={isDownloading}
          >
            {t("Download PDF", "ڈاؤن لوڈ PDF")}
          </Button>
        </div>

        <div className="flex flex-col md:flex-row items-center gap-4 w-full">
          <Input
            isClearable
            className="w-full md:max-w-[320px]"
            placeholder={t("SearchInventory")}
            size="md"
            startContent={<SearchIcon className="text-default-400" />}
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            onClear={() => handleSearch("")}
          />

          <div className="flex flex-row items-center gap-2 w-full md:justify-end">
            <div className="flex-none">
              <DatePicker
                selected={startDate}
                onChange={(date: Date | null) => {
                  setStartDate(date);
                  filterInventoryItems(inventoryItems, searchQuery, date, endDate);
                }}
                placeholderText={t("StartDate")}
                maxDate={endDate || new Date()}
                dateFormat="dd/MM/yyyy"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                className="p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div className="flex-none">
              <DatePicker
                selected={endDate}
                onChange={(date: Date | null) => {
                  setEndDate(date);
                  filterInventoryItems(inventoryItems, searchQuery, startDate, date);
                }}
                placeholderText={t("EndDate")}
                minDate={startDate ? startDate : undefined}
                maxDate={new Date()}
                dateFormat="dd/MM/yyyy"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                className="p-2 border border-gray-300 rounded-md"
              />
            </div>
            <Button
              className="min-w-[110px] flex-none"
              color="default"
              size="md"
              variant="flat"
              onClick={handleRefresh}
            >
              {t("ResetFilters")}
            </Button>
          </div>
        </div>
      </div>

      {/* Filter active indicator */}
      {(searchQuery || startDate || endDate) && (
        <div className="mb-4 p-2 bg-blue-50 rounded text-sm text-blue-700 flex items-center mx-8">
          <svg
            className="h-5 w-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
            />
          </svg>
          <span>
            {t("FilteringBy")}:
            {searchQuery && (
              <span className="ml-1 font-medium">{t("BookName")}</span>
            )}
            {startDate && (
              <span className="ml-1 font-medium">{t("StartDate")}</span>
            )}
            {endDate && (
              <span className="ml-1 font-medium">{t("EndDate")}</span>
            )}
          </span>
        </div>
      )}

      {/* Error and Loading States */}
      {error && (
        <div className="row container px-8 mb-4">
          <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            <p>{error}</p>
            <button
              className="mt-2 px-4 py-1 bg-red-200 rounded hover:bg-red-300"
              onClick={handleRefresh}
            >
              {t("TryAgain")}
            </button>
          </div>
        </div>
      )}

      {/* Inventory Table */}
      <section className="row container px-8 flex flex-col items-center justify-center gap-4 py-2">
        <DataTable
          columns={columns}
          data={filteredItems}
          isLoading={isLoading}
          emptyContent={searchQuery || startDate || endDate
            ? t("NoItemsMatchFilters")
            : t("NoInventoryItems")}
          sortDescriptor={sortDescriptor}
          onSortChange={handleSortChange}
          className="w-full"
          addSerialNumbers={true}
        />
      </section>
    </DefaultLayout>
  );
};

export default InventoryPage;
